import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { BriefcaseIcon, UserIcon } from '@heroicons/react/24/solid'
import QRious from 'qrious'

// Components
import { AuthGradient } from '../../components/AuthGradient'
import { AuthSplash } from '../../components/AuthSplash'
import { Button } from '../../components/Button'
import { OrganizationIcon } from '../../components/OrganizationIcon'
import { TextInput } from '../../components/TextInput'

// Images
import EventstackLogo from '../../assets/images/eventstack_logo.svg'
import SMS from '../../assets/images/sms.svg'

// Service
import { getSelfRegistrationDetails, createSelfRegistration } from '../../services/events.service'

// Utils
import { toast } from '../../utils/helpers'

const QR_SIZE = 200

const EventSelfRegistration = () => {
  const { eventId } = useParams()

  const [initialLoading, setInitialLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [eventDetails, setEventDetails] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [verificationId, setVerificationId] = useState(null)

  const handleError = (m) => toast(m, 'error')

  const qrCodeSrc = useMemo(() => {
    if (!verificationId) return null
    const qr = new QRious({ size: QR_SIZE, value: String(verificationId) })
    return qr.toDataURL()
  }, [verificationId])

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm()

  useEffect(() => {
    const loadEventDetails = async () => {
      const data = await getSelfRegistrationDetails(eventId, setError, setInitialLoading)

      if (data) {
        setEventDetails(data)
      }
    }

    loadEventDetails()
  }, [eventId])

  const onSubmit = async (data) => {
    await createSelfRegistration(eventId, data, handleError, setLoading, (response) => {
      setVerificationId(response.verificationId)
      setSubmitted(true)
    })
  }

  return (
    <div className="flex h-screen w-screen">
      <div className="hidden xl:block xl:basis-1/2">
        <AuthSplash />
      </div>

      <div className="relative flex h-full basis-full flex-col justify-between overflow-hidden bg-[#181823] px-8 py-12 text-white xl:basis-1/2">
        <AuthGradient />

        <div className="w-64 self-center sm:ml-8 sm:w-[184px] sm:self-start">
          {eventDetails?.logo ? (
            <img src={eventDetails.logo} alt="Event Logo" className="h-20 rounded-xl" />
          ) : (
            <img src={EventstackLogo} alt="Eventstack" />
          )}
        </div>

        {initialLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <span className="text-2xl font-bold">Loading...</span>
          </div>
        ) : error ? (
          <div className="flex flex-1 items-center justify-center">
            <span className="text-center text-2xl font-bold">{error}</span>
          </div>
        ) : submitted ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="max-w-[675px] sm:mx-auto sm:w-2/3">
              <h2 className="text-center text-3xl font-bold">
                You've been registered! Thank you.
              </h2>
              <p className="mt-4 text-center text-lg">
                Welcome to {eventDetails?.name}. You're all set.
              </p>
              {qrCodeSrc && (
                <div className="mt-8 flex justify-center">
                  <img src={qrCodeSrc} alt="Your QR Code" width={QR_SIZE} height={QR_SIZE} className="rounded-lg" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-[675px] sm:mx-auto sm:w-2/3">
            <h2 className="text-3xl font-bold">
              Register for {eventDetails?.name}
            </h2>

            <form
              className="z-1 relative mt-8"
              id="self-register-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="space-y-10">
                <div className="flex w-full flex-row gap-4">
                  <TextInput
                    className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                    error={errors.firstName && 'This field is required'}
                    autoComplete="given-name"
                    id="firstName"
                    icon={
                      <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                        <UserIcon className="h-5 fill-white" />
                      </div>
                    }
                    fullWidth
                    inputStyles="bg-transparent text-white text-md sm:text-base"
                    name="firstName"
                    placeholder="First Name"
                    {...register('firstName', { required: true })}
                  />

                  <TextInput
                    className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                    error={errors.lastName && 'This field is required'}
                    autoComplete="family-name"
                    id="lastName"
                    icon={
                      <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                        <UserIcon className="h-5 fill-white" />
                      </div>
                    }
                    fullWidth
                    inputStyles="bg-transparent text-white text-md sm:text-base"
                    name="lastName"
                    placeholder="Last Name"
                    {...register('lastName', { required: true })}
                  />
                </div>

                <TextInput
                  className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                  error={errors.email && 'This field is required'}
                  autoComplete="email"
                  id="email"
                  icon={
                    <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                      <img src={SMS} alt="email" className="h-5 stroke-purple" />
                    </div>
                  }
                  fullWidth
                  inputStyles="bg-transparent text-white text-md sm:text-base"
                  name="email"
                  placeholder="Email"
                  type="email"
                  {...register('email', { required: true })}
                />

                <TextInput
                  className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                  error={errors.companyName && 'This field is required'}
                  autoComplete="organization"
                  id="companyName"
                  icon={
                    <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                      <OrganizationIcon className="h-5 stroke-white" />
                    </div>
                  }
                  fullWidth
                  inputStyles="bg-transparent text-white text-md sm:text-base"
                  name="companyName"
                  placeholder="Company"
                  {...register('companyName', { required: true })}
                />

                <TextInput
                  className="flex h-12 w-full rounded-md border-gray bg-gray py-2.5 pl-14 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple sm:h-[60px] sm:pl-24"
                  autoComplete="organization-title"
                  id="title"
                  icon={
                    <div className="flex h-full w-10 items-center justify-center border-r border-gray-700 px-2 sm:w-[72px] sm:px-4">
                      <BriefcaseIcon className="h-5 fill-white" />
                    </div>
                  }
                  fullWidth
                  inputStyles="bg-transparent text-white text-md sm:text-base"
                  name="title"
                  placeholder="Job Title (optional)"
                  {...register('title')}
                />
              </div>

              <div className="mb-12 mt-4" />

              <Button
                className="text-md flex h-12 shrink-0 items-center rounded-md border-purple sm:h-[60px] sm:text-base"
                fullWidth
                label="Register"
                loading={loading}
                type="submit"
              />
            </form>
          </div>
        )}

        <div className="flex items-center justify-center gap-x-3 text-sm sm:justify-start sm:pl-8">
          <span>Powered by</span>
          <img src={EventstackLogo} alt="Eventstack" className="h-6" />
        </div>
      </div>
    </div>
  )
}

export default EventSelfRegistration
